import React, { useState, useCallback } from 'react';
import { CheckCircle2, Loader2, KeyRound } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { adminUpdateCredentials } from '../../api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const initialForm = { current_password: '', new_username: '', new_password: '', confirm_password: '' };

const CredentialsForm = () => {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (!form.new_username && !form.new_password) {
      toast({ title: 'Nothing to change', description: 'Provide a new username and/or password.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await adminUpdateCredentials({
        current_password: form.current_password,
        new_username: form.new_username || undefined,
        new_password: form.new_password || undefined,
      });
      localStorage.setItem('iv_admin_token', res.access_token);
      await refresh();
      toast({ title: 'Credentials updated', description: 'Use the new credentials on next sign in.' });
      setForm(initialForm);
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [form, refresh, toast]);

  return (
    <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-7">
      <div className="flex items-center gap-3 mb-2">
        <KeyRound className="w-5 h-5 text-cyan-400" />
        <h3 className="text-xl font-semibold text-white">Change Credentials</h3>
      </div>
      <p className="text-sm text-slate-400 mb-6">Update the admin username and/or password. You&apos;ll stay signed in with the new credentials.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-slate-300 text-xs mb-1.5 block">Current Password</Label>
          <Input required type="password" value={form.current_password} onChange={e => setForm({ ...form, current_password: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" autoComplete="current-password" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-xs mb-1.5 block">New Username <span className="text-slate-500">(optional)</span></Label>
            <Input value={form.new_username} onChange={e => setForm({ ...form, new_username: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" placeholder={user?.username} minLength={3} maxLength={64} autoComplete="username" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs mb-1.5 block">New Password <span className="text-slate-500">(min 8 chars)</span></Label>
            <Input type="password" value={form.new_password} onChange={e => setForm({ ...form, new_password: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" minLength={8} maxLength={128} autoComplete="new-password" />
          </div>
        </div>
        <div>
          <Label className="text-slate-300 text-xs mb-1.5 block">Confirm New Password</Label>
          <Input type="password" value={form.confirm_password} onChange={e => setForm({ ...form, confirm_password: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" autoComplete="new-password" />
        </div>
        <Button type="submit" disabled={saving} className="bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold rounded-md h-11 px-6">
          {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Update Credentials</>)}
        </Button>
      </form>
      <p className="text-xs text-slate-500 mt-4">Currently signed in as <span className="text-slate-300 font-medium">{user?.username}</span></p>
    </div>
  );
};

export default CredentialsForm;
